'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import SiteLogo from './SiteLogo';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';
import CurrencySelector from '../forms/CurrencySelector';

const NAV_LINKS = [
  { href: '/blogs', label: 'Blog' },
  { href: '/faq', label: 'FAQ' },
  { href: '/support', label: 'Support' },
];

const LEGAL_LINKS = [
  { href: '/privacy-policy', label: 'Privacy Policy' },
  { href: '/terms-of-service', label: 'Terms of Service' },
  { href: '/refund-policy', label: 'Refund Policy' },
];

export default function Navbar() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    
    // Check initial scroll position
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const isActive = (href) => {
    if (!mounted || pathname == null) return false;
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <nav
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? 'border-b border-slate-200/50 bg-white/80 backdrop-blur-md shadow-sm'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      {/* Bar */}
      <div className="custom-mx mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 lg:h-20">
        <SiteLogo size="md" priority mode="wordmark" />

        {/* Desktop: links & currency */}
        <div className="hidden md:flex items-center gap-4">
          <div className="flex items-center gap-1 relative">
            {NAV_LINKS.map(({ href, label }) => {
              const active = isActive(href);
              return (
                <Link
                  key={href}
                  href={href}
                  prefetch={false}
                  aria-current={active ? 'page' : undefined}
                  onMouseEnter={() => setHoveredLink(href)}
                  onMouseLeave={() => setHoveredLink(null)}
                  className={`relative px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                    active ? 'text-primary' : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  <span className="relative z-10">{label}</span>
                  
                  {/* Active Underline Indicator */}
                  {active && (
                    <span
                      className="absolute inset-x-4 bottom-0 h-0.5 bg-primary rounded-full animate-slide-up-fade"
                    />
                  )}

                  {/* Hover Background Pill */}
                  {hoveredLink === href && !active && (
                    <span
                      className="absolute inset-0 bg-slate-100/70 rounded-lg -z-0 animate-slide-up-fade"
                    />
                  )}
                </Link>
              );
            })}
          </div>

          <div aria-hidden="true" className="h-4 w-px bg-slate-200/50" />
          <CurrencySelector />
        </div>

        {/* Mobile: currency selector + hamburger */}
        <div className="flex items-center gap-2.5 md:hidden">
          <CurrencySelector />
          <button
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            className="relative inline-flex items-center justify-center w-11 h-11 text-slate-700 hover:text-primary active:scale-95 transition-all"
          >
            {menuOpen ? (
              <X className="w-6 h-6" strokeWidth={2.5} />
            ) : (
              <Menu className="w-6 h-6" strokeWidth={2.5} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile full-screen menu */}
      <div
        className={`md:hidden fixed left-0 right-0 z-40 bg-white transition-all duration-300 ${
          menuOpen ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none -translate-y-2'
        }`}
        style={{ top: '64px', height: 'calc(100dvh - 64px)' }}
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 px-6 py-6 overflow-y-auto">
            {/* Primary links */}
            <div className="flex flex-col gap-1 mb-5">
              <Link
                href="/"
                onClick={() => setMenuOpen(false)}
                prefetch={false}
                className={`px-4 py-3 rounded-md text-base font-medium transition-colors ${isActive('/') ? 'text-primary bg-tertiary' : 'text-gray-700 hover:text-primary hover:bg-slate-50'}`}
              >
                Home
              </Link>
              {NAV_LINKS.map(({ href, label }) => (
                <Link
                  key={href}
                  href={href}
                  prefetch={false}
                  onClick={() => setMenuOpen(false)}
                  className={`px-4 py-3 rounded-md text-base font-medium transition-colors ${isActive(href) ? 'text-primary bg-tertiary' : 'text-gray-700 hover:text-primary hover:bg-slate-50'}`}
                >
                  {label}
                </Link>
              ))}
            </div>

            {/* Divider + legal */}
            <div className="h-px bg-slate-100 my-5" />

            <div className="flex flex-col gap-1">
              {LEGAL_LINKS.map(({ href, label }) => (
                <Link
                  key={href}
                  href={href}
                  prefetch={false}
                  onClick={() => setMenuOpen(false)}
                  className="px-4 py-2.5 rounded-lg text-sm text-gray-600 hover:text-primary hover:bg-slate-50 transition-colors"
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>

          {/* Bottom */}
          <div className="border-t border-slate-100 px-6 py-4">
            <p className="text-xs text-gray-600" suppressHydrationWarning>
              © {new Date().getFullYear()} VisaProofs
            </p>
          </div>
        </div>
      </div>
    </nav>
  );
}
